import type { ProcessedFilters } from '@app/data/clients/rbServerSideClient/queries/GetSearchResults/types';
import type { GetSearchResultsFiltersVariables } from '@app/data/clients/rbServerSideClient/types';
import { SEARCH_RESULTS_FILTERS_PUBLIC_ENDPOINT } from '@app/pages/api/constants';
import type { SearchResultsFiltersAPIResponse } from '@app/pages/api/search-results-filters/types';
import { getSafeUrl } from '@redbubble/utils/getSafeUrl';
import { Dispatch, SetStateAction } from 'react';
import type { FilterState, SetFiltersType } from '../types';

const fetchFilters = async (
  variables: GetSearchResultsFiltersVariables
): Promise<ProcessedFilters> => {
  const url = getSafeUrl(
    SEARCH_RESULTS_FILTERS_PUBLIC_ENDPOINT,
    window.location.href
  );

  if (!url) {
    throw new Error('Invalid URL for search results filters endpoint');
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(variables),
  });

  const data: SearchResultsFiltersAPIResponse = await response.json();
  if (!response.ok || 'error' in data) {
    throw new Error(
      'error' in data ? data.error : 'Failed to fetch search results filters'
    );
  }
  return data;
};

type RefreshSrpFiltersType = {
  callbackVariables: {
    variables: GetSearchResultsFiltersVariables;
    url: string;
    checked?: boolean;
    button?: string;
  };
  cache: Map<string, FilterState>;
  setFilters: Dispatch<SetStateAction<SetFiltersType>>;
};

export const refreshSrpFilters = ({
  callbackVariables: { variables, url, checked, button },
  setFilters,
  cache,
}: RefreshSrpFiltersType) => {
  const cachedFilters = cache.get(url);

  if (cachedFilters) {
    setFilters({
      ...cachedFilters,
      optimisticOption: undefined,
      loading: false,
    });
  } else {
    setFilters((prevState) => ({
      filters: prevState.filters,
      optimisticOption: { name: button ?? '', checked: checked ?? false },
      loading: true,
    }));

    fetchFilters(variables)
      .then((res) => {
        cache.set(url, {
          filters: res,
        });

        setFilters({
          filters: res,
          optimisticOption: undefined,
          loading: false,
        });
      })
      .catch(() => {
        // TODO: Handle errors
        setFilters({
          filters: null,
          optimisticOption: undefined,
          loading: false,
        });
      });
  }
};
